import { FC, lazy, Suspense, useEffect, useState, type ComponentType } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import { LoginTab } from './LoginTab'
import { CreateAccountTab } from './CreateAccountTab'
import useAuthStore from '@/auth/authStore'
import ForgotPassword from './forgotPassword'
import { Logo } from '@/common/assets/svg'
import { H3 } from '@/common/ui'
import SolidBackgroundModal from '@/common/ui/SolidBackgroundModal'
import { usePreloadCampaignsAndImages } from '@/campaigns/usePreloadCampaignsAndImages'
import { usePreloadOnboardingContainer } from '@/onboarding/usePreloadOnboardingContainer'
import { useLocalFlags } from '@/common/featureFlags/local/useLocalFlags'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { ONBOARDING_COPY } from '@/locales/en'
import { Navigate, createSearchParams, useNavigate } from 'react-router-dom'
import { APP_ROUTES } from '@/common/constants'
import { AuthTab } from './types'
import type { LoginV2Props } from './features/login-v2/components/LoginV2'
import type { SignupV2Props } from './features/signup-v2/components/SignupV2'
import type { ForgotPasswordV2Props } from './features/forgot-password-v2/components/ForgotPasswordV2'

// Keep this guard inline here, duplicated per dev-only lazy import across the codebase,
// rather than switching to a shared constant — see src/common/env.ts (IS_DEV vs
// IS_LOCAL_DEV_SERVER) and don't change this without rebuilding and checking build/assets.
let LazyLoginV2: ComponentType<LoginV2Props> | undefined
let LazySignupV2: ComponentType<SignupV2Props> | undefined
let LazyForgotPasswordV2: ComponentType<ForgotPasswordV2Props> | undefined

if (import.meta.env.DEV || import.meta.env.VITE_ENV === 'dev') {
    LazyLoginV2 = lazy(() => import('./features/login-v2/components/LoginV2').then((m) => ({ default: m.LoginV2 })))
    LazySignupV2 = lazy(() => import('./features/signup-v2/components/SignupV2').then((m) => ({ default: m.SignupV2 })))
    LazyForgotPasswordV2 = lazy(() =>
        import('./features/forgot-password-v2/components/ForgotPasswordV2').then((m) => ({
            default: m.ForgotPasswordV2,
        })),
    )
}

type TabProps = {
    label: string
    value: string
}

const Tab: FC<TabProps> = ({ label, value }) => {
    return (
        <TabsTrigger
            value={value}
            className="data-[state=active]:font-semibold text-foreground w-full py-3 rounded-b-none data-[state=active]:bg-secondary-light"
        >
            {label}
        </TabsTrigger>
    )
}

export const AuthScreen = ({ tab }: { tab?: AuthTab }) => {
    const defaultTab = tab ?? AuthTab.LOGIN

    const [activeTab, setActiveTab] = useState<AuthTab>(defaultTab)
    // keep local state in sync when `tab` prop changes (route navigation)
    useEffect(() => {
        setActiveTab(tab || AuthTab.LOGIN)
    }, [tab])
    const { isForgotPassword, setIsForgotPassword } = useAuthStore()
    const authDetails = useAuthStore((s) => s.authDetails)
    const { showOnboardingV2 } = useLocalFlags()
    const navigate = useNavigate()

    const handleTabChange = (value: string) => {
        setActiveTab(value as AuthTab)
    }

    const hideResetPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        setIsForgotPassword(false)
    }

    // Where an unverified login (LoginV2) and a signup awaiting confirmation (SignupV2)
    // both land — the page owns this routing decision, not the components below it.
    const navigateToConfirmUser = (email: string) =>
        navigate({
            pathname: APP_ROUTES.CONFIRM_USER,
            search: createSearchParams({ username: email }).toString(),
        })

    // Preload campaigns while user is on the auth screen
    usePreloadCampaignsAndImages()
    usePreloadOnboardingContainer(activeTab === AuthTab.SIGNUP)

    if (authDetails) return <Navigate to={APP_ROUTES.DASHBOARD} replace />

    if (showOnboardingV2 && (LazyLoginV2 || LazySignupV2 || LazyForgotPasswordV2)) {
        return (
            <Suspense fallback={<FullScreenLoader dark />}>
                {isForgotPassword && LazyForgotPasswordV2 ? (
                    <LazyForgotPasswordV2 onSignInClick={() => setIsForgotPassword(false)} />
                ) : activeTab === AuthTab.SIGNUP && LazySignupV2 ? (
                    <LazySignupV2
                        onLoginClick={() => navigate(APP_ROUTES.DASHBOARD)}
                        onSignUpConfirmationRequired={navigateToConfirmUser}
                    />
                ) : (
                    LazyLoginV2 && (
                        <LazyLoginV2
                            onForgotPasswordClick={() => setIsForgotPassword(true)}
                            onSignUpClick={() => navigate(APP_ROUTES.SIGNUP)}
                            onUnverifiedUser={navigateToConfirmUser}
                        />
                    )
                )}
            </Suspense>
        )
    }

    return (
        <SolidBackgroundModal>
            <div className="flex flex-col gap-6">
                <Logo className="h-[34px] md:h-[40px] mx-auto" />
                {isForgotPassword ? (
                    <ForgotPassword onSignInClick={hideResetPassword} />
                ) : (
                    <>
                        {activeTab === 'login' && (
                            <H3 className="text-center text-3xl">{ONBOARDING_COPY.login.loginTitle}</H3>
                        )}
                        {activeTab === 'signup' && (
                            <>
                                <H3 className="text-center mb-0 text-3x leading-9">
                                    {ONBOARDING_COPY.login.signupTitle}
                                </H3>
                                <p className="text-sm text-center">{ONBOARDING_COPY.login.signupDescription}</p>
                            </>
                        )}

                        <Tabs value={activeTab} onValueChange={handleTabChange}>
                            <TabsList className="p-0 h-auto bg-background flex justify-center">
                                <Tab label="Log in" value="login" />
                                <Tab label="Create an account" value="signup" />
                            </TabsList>
                            <div className="h-[2px] bg-primary-light" />
                            <TabsContent tabIndex={-1} className="pt-4" value="login">
                                <LoginTab />
                            </TabsContent>
                            <TabsContent tabIndex={-1} className="pt-4" value="signup">
                                <CreateAccountTab />
                            </TabsContent>
                        </Tabs>
                    </>
                )}
            </div>
        </SolidBackgroundModal>
    )
}
